import { Injectable, Signal, inject, DestroyRef } from '@angular/core';
import { patchState, signalState, SignalState } from '@ngrx/signals';
import { defaultApiGatewayParams } from '@lentau/core/api/gateway';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SeoPatternState, SeoRedirectState, SeoState } from './interfaces';
import { SEMService } from 'angular-api-gateway/v1/api/sEM.service';
import { SemUsePatternResponse } from 'angular-api-gateway/v1/model/semUsePatternResponse';
import { SemUsePatternRequest } from 'angular-api-gateway/v1/model/semUsePatternRequest';
import { BadRequestResponse } from 'angular-api-gateway/v1/model/badRequestResponse';
import { SemGetRedirectResponse } from 'angular-api-gateway/v1/model/semGetRedirectResponse';
import { PatternTypeEnum } from '@lentau/seo/shared/constants';
import { shouldSkipSemRedirect } from '@lentau/seo/shared/should-skip-sem-redirect.helper';
import { ResponseErrorListItemProps } from '@lentau/api/shared/monolith/types';

const initialSeoState: SeoState = {
  pattern: {
    data: null,
    patternLoading: false,
    errorMessage: null,
  },
  redirect: {
    data: null,
    redirectLoading: false,
  },
};

@Injectable({ providedIn: 'root' })
export class SemSeoStore {
  private semService: SEMService = inject(SEMService);
  private destroyRef: DestroyRef = inject(DestroyRef);

  private state: SignalState<SeoState> = signalState(initialSeoState);

  public pattern: Signal<SeoPatternState> = this.state.pattern;
  public redirect: Signal<SeoRedirectState> = this.state.redirect;

  public semUsePattern(params: SemUsePatternRequest): void {
    patchState(this.state, { pattern: { ...this.state.pattern(), patternLoading: true } });

    this.semService
      .semUsepattern({ ...defaultApiGatewayParams, semUsePatternRequest: { ...params } })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: SemUsePatternResponse) =>
          patchState(this.state, { pattern: { ...this.state.pattern(), patternLoading: false, data: response } }),

        error: (errorList: ResponseErrorListItemProps[]) => {
          this.clearState();

          if (
            errorList?.length &&
            errorList[0]?.Code === BadRequestResponse.CodeEnum.BadRequest &&
            params?.patternType !== PatternTypeEnum.Default
          ) {
            this.semUsePattern({ ...params, patternType: PatternTypeEnum.Default });
          }
        },
      });
  }

  public getRedirectInfo(url: string): void {
    if (shouldSkipSemRedirect(url)) {
      return;
    }

    patchState(this.state, { redirect: { ...this.state.redirect(), redirectLoading: true } });

    this.semService
      .getSemRedirect({ ...defaultApiGatewayParams, uRLPath: url })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: SemGetRedirectResponse) =>
          patchState(this.state, { redirect: { ...this.state.redirect(), data: response, redirectLoading: false } }),

        error: () => patchState(this.state, { redirect: { data: null, redirectLoading: false } }),
      });
  }

  public clearState(): void {
    patchState(this.state, initialSeoState);
  }
}
